// Splits a chat message body into plain-text and "@<recipient/role name>" segments,
// so the mentions can be rendered as inline, clickable chips.

export interface MentionableUser {
  id: string;
  name: string;
}

export interface MentionableRole {
  id: number;
  name: string;
}

export interface BodySegment {
  text: string;
  // Set when the segment is an "@<user>" mention.
  mentionUserId?: string;
  // Set when the segment is an "@<role>" mention.
  mentionRoleId?: number;
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function splitBodyMentions(
  body: string,
  recipients: MentionableUser[],
  roles: MentionableRole[] = []
): BodySegment[] {
  type Named = { kind: 'user'; id: string; name: string } | { kind: 'role'; id: number; name: string };

  // Longest names first so "@Anna Berg" wins over a shorter "@Anna", and so
  // "@Developer" wins over a hypothetical "@Dev" role.
  const named: Named[] = [
    ...recipients.filter((u) => u.name.length > 0).map((u) => ({ kind: 'user' as const, id: u.id, name: u.name })),
    ...roles.filter((r) => r.name.length > 0).map((r) => ({ kind: 'role' as const, id: r.id, name: r.name })),
  ].sort((a, b) => b.name.length - a.name.length);

  if (named.length === 0) return [{ text: body }];

  const regex = new RegExp(`@(?:${named.map((n) => escapeRegExp(n.name)).join('|')})`, 'g');
  const segments: BodySegment[] = [];
  let lastIndex = 0;

  for (const match of body.matchAll(regex)) {
    const start = match.index ?? 0;
    if (start > lastIndex) segments.push({ text: body.slice(lastIndex, start) });

    const name = match[0].slice(1);
    const hit = named.find((n) => n.name === name);
    if (hit?.kind === 'user') {
      segments.push({ text: match[0], mentionUserId: hit.id });
    } else if (hit?.kind === 'role') {
      segments.push({ text: match[0], mentionRoleId: hit.id });
    } else {
      segments.push({ text: match[0] });
    }
    lastIndex = start + match[0].length;
  }
  if (lastIndex < body.length) segments.push({ text: body.slice(lastIndex) });

  return segments;
}

/**
 * Strips a user's "@mention" segments from a body, leaving everyone else's intact.
 *
 * A literal `body.replace(\`@${name}\`, '')` matches the first occurrence of that
 * *text*, which may be the opening of a longer name: removing "Ann" from
 * "@Anna Berg @Ann" ate the front of Anna's mention and left the real "@Ann"
 * behind. Splitting first resolves each "@" to a single user (longest name wins),
 * so only the segments that are genuinely this user's are dropped.
 *
 * Pass every mentionable user, not just the tagged ones — a name typed by hand
 * rather than picked from the dropdown still has to be recognised as its own
 * mention, or it is exactly the longer name that gets clipped.
 */
export function removeUserMentions(
  body: string,
  users: MentionableUser[],
  roles: MentionableRole[],
  userId: string
): string {
  return splitBodyMentions(body, users, roles)
    .filter((segment) => segment.mentionUserId !== userId)
    .map((segment) => segment.text)
    .join('')
    .replace(/ {2,}/g, ' ')
    .trimStart();
}

/**
 * Splits a filter box's text into the part that searches messages and a trailing
 * "@…" naming a person, e.g. `designer @fel` → `{ search: 'designer', mention: 'fel' }`.
 *
 * `mention` is null when there is no "@" at all, and `''` for a bare "@" — the
 * difference matters: a bare "@" is a request for the whole people list, while
 * no "@" means the caller should fall back to matching names against `search`.
 *
 * The "@" has to open a word. Mid-word it belongs to whatever is being typed:
 * `john@acme.com` is an address someone is searching for, not a request for the
 * people list, and splitting it searched bodies for the far broader "john".
 */
export function splitSearchMention(input: string): { search: string; mention: string | null } {
  const match = /(?:^|\s)@([^@]*)$/.exec(input);
  if (!match) return { search: input.trim(), mention: null };
  return { search: input.slice(0, match.index).trim(), mention: match[1] };
}

/** Whether the body names a specific user — used to single out a row addressed at you. */
export function bodyMentionsUser(
  body: string,
  recipients: MentionableUser[],
  roles: MentionableRole[],
  userId: string
): boolean {
  return splitBodyMentions(body, recipients, roles).some((segment) => segment.mentionUserId === userId);
}
